import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  SwapHoriz as StatusIcon,
  Inventory as ProcessIcon,
} from '@mui/icons-material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PurchaseService } from '../../../services/purchaseService';
import { warehouseService } from '../../../services/warehouseService';
import { PurchaseReturn } from '../../../types/purchase';
import { formatCurrency } from '../../../utils/formatters';

const NEXT_STATUSES: Record<string, string[]> = {
  draft: ['sent', 'closed'],
  sent: ['acknowledged', 'closed'],
  acknowledged: ['closed'],
  processed: ['closed'],
  closed: [],
};

export const PurchaseReturnDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [purchaseReturn, setPurchaseReturn] = useState<PurchaseReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [processWarehouseId, setProcessWarehouseId] = useState('');

  useEffect(() => {
    if (id) {
      fetchReturn();
    }
  }, [id]);

  useEffect(() => {
    if (!processDialogOpen) return;
    warehouseService.getWarehouses()
      .then((data) => {
        setWarehouses(data);
        setProcessWarehouseId((prev) => prev || data[0]?.id || '');
      })
      .catch(() => setWarehouses([]));
  }, [processDialogOpen]);

  const fetchReturn = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await PurchaseService.getPurchaseReturn(id);
      setPurchaseReturn(data);
    } catch (error) {
      console.error('Failed to fetch return:', error);
      enqueueSnackbar('Failed to load purchase return details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!purchaseReturn || !newStatus) return;
    try {
      setActionLoading(true);
      await PurchaseService.updatePurchaseReturnStatus(purchaseReturn.id, newStatus, statusNotes || undefined);
      enqueueSnackbar('Status updated successfully', { variant: 'success' });
      setStatusDialogOpen(false);
      setStatusNotes('');
      fetchReturn();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!purchaseReturn || !processWarehouseId) return;
    try {
      setActionLoading(true);
      await PurchaseService.processPurchaseReturn(purchaseReturn.id, processWarehouseId);
      enqueueSnackbar('Return processed — stock has been reduced', { variant: 'success' });
      setProcessDialogOpen(false);
      fetchReturn();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to process return', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    const colors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
      draft: 'default',
      sent: 'primary',
      acknowledged: 'primary',
      processed: 'success',
      closed: 'default',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!purchaseReturn) {
    return (
      <Box>
        <Typography>Purchase return not found</Typography>
      </Box>
    );
  }

  const nextStatuses = NEXT_STATUSES[purchaseReturn.status] || [];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/purchase/returns')}>
            Back
          </Button>
          <Typography variant="h4">Return {purchaseReturn.returnNumber}</Typography>
          <Chip
            label={purchaseReturn.status.toUpperCase()}
            color={getStatusColor(purchaseReturn.status)}
          />
        </Box>
        <Box display="flex" gap={1}>
          {purchaseReturn.status === 'acknowledged' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ProcessIcon />}
              onClick={() => setProcessDialogOpen(true)}
              disabled={actionLoading}
            >
              Process Return
            </Button>
          )}
          {nextStatuses.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<StatusIcon />}
              onClick={() => {
                setNewStatus(nextStatuses[0]);
                setStatusDialogOpen(true);
              }}
              disabled={actionLoading}
            >
              Update Status
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Return Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Return Number</Typography>
                <Typography variant="body1">{purchaseReturn.returnNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Bill Number</Typography>
                <Typography variant="body1">{(purchaseReturn as any).purchaseBillNumber || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Return Date</Typography>
                <Typography variant="body1">{new Date(purchaseReturn.returnDate).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Reason</Typography>
                <Typography variant="body1">{purchaseReturn.returnReason?.replace('_', ' ') || '-'}</Typography>
              </Grid>
              {purchaseReturn.returnDescription && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{purchaseReturn.returnDescription}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Supplier Information
            </Typography>
            <Typography variant="body1">{(purchaseReturn as any).supplierName || '-'}</Typography>
            {purchaseReturn.debitNoteNumber && (
              <Typography variant="body2" color="text.secondary">
                Debit Note: {purchaseReturn.debitNoteNumber}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Items
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Tax</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseReturn.items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {(item as any).productName
                          ? `${(item as any).productName}${(item as any).variantName && (item as any).variantName !== 'Standard' ? ` - ${(item as any).variantName}` : ''}`
                          : (item as any).variantName || '-'}
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.taxAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.lineTotal)}</TableCell>
                    </TableRow>
                  ))}
                  {(!purchaseReturn.items || purchaseReturn.items.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">No items found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Return Summary
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}><Typography variant="body2">Subtotal:</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" align="right">{formatCurrency(purchaseReturn.subtotal)}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2">Tax:</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" align="right">{formatCurrency(purchaseReturn.taxAmount)}</Typography></Grid>
              <Grid item xs={6}><Typography variant="h6">Total:</Typography></Grid>
              <Grid item xs={6}><Typography variant="h6" align="right">{formatCurrency(purchaseReturn.totalAmount)}</Typography></Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Return Status</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>New Status</InputLabel>
              <Select label="New Status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                {nextStatuses.map((s) => (
                  <MenuItem key={s} value={s}>{s.toUpperCase()}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Notes (optional)"
              multiline
              rows={3}
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateStatus} variant="contained" disabled={actionLoading || !newStatus}>
            {actionLoading ? <CircularProgress size={24} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={processDialogOpen} onClose={() => setProcessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Return</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Choose the warehouse these goods are leaving from — stock will be reduced there.
            </Alert>
            <FormControl fullWidth>
              <InputLabel>Warehouse</InputLabel>
              <Select
                label="Warehouse"
                value={processWarehouseId}
                onChange={(e) => setProcessWarehouseId(e.target.value)}
              >
                {warehouses.map((wh) => (
                  <MenuItem key={wh.id} value={wh.id}>{wh.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleProcess}
            variant="contained"
            disabled={actionLoading || !processWarehouseId}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Process Return'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
